import { z } from 'zod';
import { isoTimestamp } from './common';
import { envKeySchema, isReservedEnvKey, isReservedMcpEnvKey } from './mapping';

// --- Names ---

export const toolNameSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]{0,63}$/,
    'Use 1–64 lowercase letters, digits or _, starting with a letter',
  );
export const resourceNameSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_-]{0,63}$/,
    'Use 1–64 lowercase letters, digits, - or _, starting with a letter',
  );
const descriptionSchema = z.string().trim().max(2000);

/** A JSON Schema object describing a tool's input (what the model fills in). */
export const toolInputSchemaSchema = z
  .record(z.string(), z.unknown())
  .refine((s) => s.type === 'object', { message: 'The input schema must have "type": "object"' })
  .refine((s) => JSON.stringify(s).length <= 32_768, {
    message: 'The input schema is limited to 32 KiB',
  });
export type ToolInputSchema = z.infer<typeof toolInputSchemaSchema>;

const headerName = z.string().regex(/^[A-Za-z0-9-]{1,100}$/, 'Invalid header name');
/** Template text: `{{input.x}}`, `{{vars.x}}`, `{{env.X}}` placeholders only. */
export const templateSchema = z.string().max(10_000);

export const httpToolConfigSchema = z
  .object({
    method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
    urlTemplate: z.string().min(1).max(2048),
    headers: z.record(headerName, z.string().max(4096)).default({}),
    bodyTemplate: templateSchema.optional(),
    timeoutMs: z.number().int().min(100).max(60_000).default(15_000),
    responseMaxBytes: z.number().int().min(1).max(1_048_576).default(262_144),
  })
  .strict();
export type HttpToolConfig = z.infer<typeof httpToolConfigSchema>;

export const cliToolConfigSchema = z
  .object({
    command: z
      .string()
      .regex(/^\/[^\0]*$/, 'Use an absolute path')
      .max(1024),
    argsTemplate: z.array(z.string().max(4096)).max(64).default([]),
    timeoutMs: z.number().int().min(100).max(120_000).default(15_000),
    outputMaxBytes: z.number().int().min(1).max(1_048_576).default(262_144),
  })
  .strict();
export type CliToolConfig = z.infer<typeof cliToolConfigSchema>;

/** A tool that runs the model's code in a sandbox (created from the sandbox, not the tools form). */
export const sandboxToolConfigSchema = z.object({ sandboxId: z.uuid() }).strict();
export type SandboxToolConfig = z.infer<typeof sandboxToolConfigSchema>;

export const toolKindSchema = z.enum(['http', 'cli', 'sandbox']);
export type ToolKind = z.infer<typeof toolKindSchema>;

const toolFields = {
  name: toolNameSchema,
  description: descriptionSchema.min(1, 'Describe what the tool does (the model reads this)'),
  inputSchema: toolInputSchemaSchema,
};

export const createToolRequestSchema = z.discriminatedUnion('kind', [
  z.object({ ...toolFields, kind: z.literal('http'), config: httpToolConfigSchema }),
  z.object({ ...toolFields, kind: z.literal('cli'), config: cliToolConfigSchema }),
]);
export type CreateToolRequest = z.infer<typeof createToolRequestSchema>;

export const updateToolRequestSchema = z
  .object({
    description: toolFields.description.optional(),
    inputSchema: toolInputSchemaSchema.optional(),
    config: z.union([httpToolConfigSchema, cliToolConfigSchema]).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateToolRequest = z.infer<typeof updateToolRequestSchema>;

export const toolDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  kind: toolKindSchema,
  inputSchema: z.record(z.string(), z.unknown()),
  config: z.union([httpToolConfigSchema, cliToolConfigSchema, sandboxToolConfigSchema]),
  pluginId: z.uuid().nullable(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type ToolDto = z.infer<typeof toolDtoSchema>;

// --- MCP servers ---

/**
 * A header or env value: a literal, a reference to an env var (secret or plain) by key, or a
 * template of env vars such as `Bearer {{env.GITHUB_TOKEN}}`.
 */
export const valueRefSchema = z.union([
  z.object({ value: z.string().max(4096) }).strict(),
  z.object({ env: envKeySchema }).strict(),
  z
    .object({
      template: z
        .string()
        .max(4096)
        .refine((t) => /\{\{\s*env\.[A-Z_][A-Z0-9_]*\s*\}\}/.test(t), {
          message: 'Use at least one {{env.NAME}} placeholder',
        }),
    })
    .strict(),
]);
export type ValueRef = z.infer<typeof valueRefSchema>;

const mcpToolFilterSchema = z.array(z.string().min(1).max(128)).max(200);
const mcpCommon = {
  name: resourceNameSchema,
  description: descriptionSchema.default(''),
  toolFilter: mcpToolFilterSchema.default([]),
};
const mcpUrlSchema = z.url({ protocol: /^https?$/ }).max(2048);
const mcpHeadersSchema = z.record(headerName, valueRefSchema);
const mcpCommandSchema = z
  .string()
  .regex(/^\/[^\0]*$/, 'Use an absolute path')
  .max(1024);
const mcpArgsSchema = z.array(z.string().max(4096)).max(64);
const mcpEnvSchema = z
  .record(envKeySchema, valueRefSchema)
  .refine((env) => Object.keys(env).every((k) => !isReservedMcpEnvKey(k)), {
    message: 'Reserved environment variables cannot be set',
  });

export const createMcpServerRequestSchema = z.discriminatedUnion('transport', [
  z.object({
    ...mcpCommon,
    transport: z.literal('streamable_http'),
    url: mcpUrlSchema,
    headers: mcpHeadersSchema.default({}),
  }),
  z.object({
    ...mcpCommon,
    transport: z.literal('stdio'),
    command: mcpCommandSchema,
    args: mcpArgsSchema.default([]),
    env: mcpEnvSchema.default({}),
  }),
]);
export type CreateMcpServerRequest = z.infer<typeof createMcpServerRequestSchema>;

/**
 * The value the API shows in place of a literal or template header/env value (they may be
 * credentials). Sending it back in an update keeps the stored value for that key.
 */
export const MASKED_REF_VALUE = '••••';

/**
 * Name and transport are fixed. `url`/`headers` apply to streamable HTTP servers and
 * `command`/`args`/`env` to stdio servers; `headers` and `env` replace the whole map.
 */
export const updateMcpServerRequestSchema = z
  .object({
    description: descriptionSchema.optional(),
    toolFilter: mcpToolFilterSchema.optional(),
    url: mcpUrlSchema.optional(),
    headers: mcpHeadersSchema.optional(),
    command: mcpCommandSchema.optional(),
    args: mcpArgsSchema.optional(),
    env: mcpEnvSchema.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateMcpServerRequest = z.infer<typeof updateMcpServerRequestSchema>;

export const mcpServerDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  transport: z.enum(['streamable_http', 'stdio']),
  url: z.string().nullable(),
  command: z.string().nullable(),
  args: z.array(z.string()),
  headers: z.record(z.string(), valueRefSchema),
  env: z.record(z.string(), valueRefSchema),
  toolFilter: z.array(z.string()),
  pluginId: z.uuid().nullable(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type McpServerDto = z.infer<typeof mcpServerDtoSchema>;

export const mcpTestResultSchema = z.object({
  ok: z.boolean(),
  message: z.string().nullable(),
  tools: z.array(z.object({ name: z.string(), description: z.string() })),
});
export type McpTestResult = z.infer<typeof mcpTestResultSchema>;

// --- Skills & plugins ---

export const createSkillRequestSchema = z.object({
  name: resourceNameSchema,
  description: descriptionSchema.default(''),
  instructions: z.string().max(20_000),
  toolIds: z.array(z.uuid()).max(50).default([]),
});
export type CreateSkillRequest = z.infer<typeof createSkillRequestSchema>;
export const updateSkillRequestSchema = z
  .object({
    description: descriptionSchema.optional(),
    instructions: z.string().max(20_000).optional(),
    toolIds: z.array(z.uuid()).max(50).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateSkillRequest = z.infer<typeof updateSkillRequestSchema>;

export const skillDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  instructions: z.string(),
  toolIds: z.array(z.uuid()),
  /** Reference files (from an imported skills folder); agents read them with `read_skill_file`. */
  files: z.array(z.object({ path: z.string(), size: z.number().int() })),
  pluginId: z.uuid().nullable(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type SkillDto = z.infer<typeof skillDtoSchema>;

/** Data-only plugin manifest. Skills reference the manifest's (or existing) tools by name. */
export const pluginManifestSchema = z.object({
  name: resourceNameSchema,
  version: z
    .string()
    .regex(/^\d+\.\d+\.\d+([-+][0-9A-Za-z.-]+)?$/, 'Use a semantic version like 1.0.0'),
  description: descriptionSchema.default(''),
  tools: z.array(createToolRequestSchema).max(50).default([]),
  skills: z
    .array(
      z.object({
        name: resourceNameSchema,
        description: descriptionSchema.default(''),
        instructions: z.string().max(20_000),
        tools: z.array(toolNameSchema).max(50).default([]),
      }),
    )
    .max(50)
    .default([]),
  mcpServers: z.array(createMcpServerRequestSchema).max(20).default([]),
});
export type PluginManifest = z.infer<typeof pluginManifestSchema>;

export const pluginDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  version: z.string(),
  description: z.string(),
  counts: z.object({
    tools: z.number().int(),
    skills: z.number().int(),
    mcpServers: z.number().int(),
  }),
  createdAt: isoTimestamp,
});
export type PluginDto = z.infer<typeof pluginDtoSchema>;

// --- Environment ---

export const envScopeSchema = z.enum(['tenant', 'agent']);
const settableEnvKey = envKeySchema.refine((k) => !isReservedEnvKey(k), {
  message: 'This variable name is reserved',
});

export const createEnvVarRequestSchema = z
  .object({
    key: settableEnvKey,
    scope: envScopeSchema.default('tenant'),
    agentId: z.uuid().optional(),
    isSecret: z.boolean().default(false),
    value: z.string().max(32_768),
  })
  .refine((v) => (v.scope === 'agent') === Boolean(v.agentId), {
    message: 'Agent-scoped variables need an agent (and tenant ones must not have one)',
    path: ['agentId'],
  });
export type CreateEnvVarRequest = z.infer<typeof createEnvVarRequestSchema>;

export const updateEnvVarRequestSchema = z.object({ value: z.string().max(32_768) });

export const envVarDtoSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  scope: envScopeSchema,
  agentId: z.uuid().nullable(),
  isSecret: z.boolean(),
  /** Plain values only; secrets are never returned. */
  value: z.string().nullable(),
  last4: z.string().nullable(),
  updatedAt: isoTimestamp,
});
export type EnvVarDto = z.infer<typeof envVarDtoSchema>;

export const importEnvRequestSchema = z.object({
  content: z.string().max(200_000),
  scope: envScopeSchema.default('tenant'),
  agentId: z.uuid().optional(),
  /** Store every imported value as a secret. */
  asSecrets: z.boolean().default(false),
  /** Replace existing values with the same key and scope. */
  overwrite: z.boolean().default(false),
});
export type ImportEnvRequest = z.infer<typeof importEnvRequestSchema>;

export const importEnvResultSchema = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.array(
    z.object({ line: z.number().int(), key: z.string().nullable(), reason: z.string() }),
  ),
});
export type ImportEnvResult = z.infer<typeof importEnvResultSchema>;

/** Parses `.env` text: `KEY=value`, `export KEY=value`, quotes, `#` comments, blank lines. */
export function parseDotEnv(content: string): {
  entries: Array<{ line: number; key: string; value: string }>;
  errors: Array<{ line: number; key: string | null; reason: string }>;
} {
  const entries: Array<{ line: number; key: string; value: string }> = [];
  const errors: Array<{ line: number; key: string | null; reason: string }> = [];
  content.split(/\r?\n/).forEach((raw, i) => {
    const line = i + 1;
    const text = raw.trim();
    if (!text || text.startsWith('#')) return;
    const match = /^(?:export\s+)?([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.*)$/.exec(text);
    if (!match) {
      errors.push({ line, key: null, reason: 'Not a KEY=value line' });
      return;
    }
    const key = match[1]!;
    let value = match[2]!;
    const quote = value[0];
    if ((quote === '"' || quote === "'") && value.endsWith(quote) && value.length >= 2) {
      value = value.slice(1, -1);
      if (quote === '"') value = value.replace(/\\n/g, '\n').replace(/\\"/g, '"');
    } else {
      value = value.replace(/\s+#.*$/, '');
    }
    if (!/^[A-Z_][A-Z0-9_]*$/.test(key))
      errors.push({ line, key, reason: 'Keys must be UPPER_CASE' });
    else if (isReservedEnvKey(key)) errors.push({ line, key, reason: 'Reserved variable name' });
    else entries.push({ line, key, value });
  });
  return { entries, errors };
}

// --- Agent version attachments & memory ---

export const memoryConfigSchema = z
  .object({ lastMessages: z.number().int().min(1).max(100).default(20) })
  .strict();
export type MemoryConfig = z.infer<typeof memoryConfigSchema>;
export const DEFAULT_MEMORY_CONFIG: MemoryConfig = { lastMessages: 20 };

export const CLAUDE_BUILTIN_TOOLS = [
  'Read',
  'Glob',
  'Grep',
  'WebSearch',
  'WebFetch',
  'Bash',
  'Write',
  'Edit',
] as const;
export const claudeBuiltinToolSchema = z.enum(CLAUDE_BUILTIN_TOOLS);
